import React, { useEffect, useMemo, useState } from "react";
import ExportToolbar from "../../components/ExportToolbar";
import Icon from "../../components/AppIcon";
import MetricCard from "./components/MetricCard";
import KPIPanel from "./components/KPIPanel";
import { useLocationContext } from "context/LocationContext";
import {
  flattenDistrictsWithRegions,
  fetchDistrictAprStatuses,
  summarizeAprWorkflow,
  filterDistrictStatusRows,
  buildRegionFilterOptions,
  APR_STATUS_FILTER_OPTIONS,
} from "./aprWorkflowStats";
import DistrictSubmissionTable from "./components/DistrictSubmissionTable";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  ASSESSMENT_STATUS_CHART_COLORS,
  DPAT_BRAND,
  DPAT_CHART,
  tooltipStyle,
} from "../dpat-dashboard/dpatChartTheme";

const APR_YEAR_START = 2020;

const APR_YEAR_OPTIONS = (() => {
  const endYear = new Date().getFullYear();
  const options = [];
  for (let year = endYear; year >= APR_YEAR_START; year -= 1) {
    options.push({ label: String(year), value: String(year) });
  }
  return options;
})();

const STAGE_BAR_COLORS = [
  ASSESSMENT_STATUS_CHART_COLORS.none,
  ASSESSMENT_STATUS_CHART_COLORS.Start,
  ASSESSMENT_STATUS_CHART_COLORS.Pending,
  ASSESSMENT_STATUS_CHART_COLORS.Completed,
  ASSESSMENT_STATUS_CHART_COLORS.Closed,
];

const WORKFLOW_STEPS = [
  { key: "Start", label: "Start", hint: "District drafting" },
  { key: "Pending", label: "Pending", hint: "RCC review" },
  { key: "Completed", label: "Completed", hint: "With NDPC" },
  { key: "Closed", label: "Closed", hint: "Finalized" },
];

const pct = (part, whole) => {
  if (!whole) return null;
  return Math.round((Number(part) / Number(whole)) * 1000) / 10;
};

const AprDashboard = () => {
  const { orgTree, isOrgTreeLoading } = useLocationContext();
  const currentYear = String(new Date().getFullYear());
  const defaultYear =
    APR_YEAR_OPTIONS.find((o) => o.value === currentYear) || APR_YEAR_OPTIONS[0];

  const [reportingYear, setReportingYear] = useState(defaultYear?.value || currentYear);
  const [statusRows, setStatusRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ percent: 0, loaded: 0, total: 0 });
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const districtRows = useMemo(() => flattenDistrictsWithRegions(orgTree), [orgTree]);
  const summary = useMemo(() => summarizeAprWorkflow(statusRows), [statusRows]);

  useEffect(() => {
    if (!districtRows.length || isOrgTreeLoading) {
      setStatusRows([]);
      setFetchProgress({ percent: 0, loaded: 0, total: 0 });
      return;
    }

    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      setStatusRows([]);
      setFetchProgress({ percent: 0, loaded: 0, total: districtRows.length });
      try {
        const rows = await fetchDistrictAprStatuses(
          districtRows,
          reportingYear,
          controller.signal,
          (progress) => {
            if (!controller.signal.aborted) setFetchProgress(progress);
          },
        );
        if (!controller.signal.aborted) setStatusRows(rows);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load APR statuses.");
        setStatusRows([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setFetchProgress({ percent: 0, loaded: 0, total: 0 });
        }
      }
    })();

    return () => controller.abort();
  }, [districtRows, reportingYear, isOrgTreeLoading]);

  useEffect(() => {
    setRegionFilter("all");
    setStatusFilter("all");
    setSearch("");
  }, [reportingYear]);

  const regionOptions = useMemo(() => buildRegionFilterOptions(statusRows), [statusRows]);

  const filteredStatusRows = useMemo(
    () => filterDistrictStatusRows(statusRows, { regionKey: regionFilter, statusFilter, search }),
    [statusRows, regionFilter, statusFilter, search],
  );

  const statusChips = useMemo(() => {
    const counts = summary.statusCounts || {};
    return [
      { key: "none", label: "Not started", count: counts.none ?? 0, color: DPAT_CHART.slate },
      { key: "Start", label: "In progress", count: counts.Start ?? 0, color: DPAT_CHART.navy },
      { key: "Pending", label: "Submitted (RCC)", count: counts.Pending ?? 0, color: DPAT_CHART.amber },
      { key: "Completed", label: "With NDPC", count: counts.Completed ?? 0, color: DPAT_CHART.emerald },
      { key: "Closed", label: "Closed", count: counts.Closed ?? 0, color: DPAT_CHART.teal },
    ];
  }, [summary.statusCounts]);

  const treeReady = !isOrgTreeLoading;
  const statusReady =
    treeReady && districtRows.length > 0 && !loading && statusRows.length === districtRows.length;

  const total = summary.totalDistricts || 0;
  const headlineMetrics = [
    {
      title: "Districts started",
      value: String(summary.districtsStarted),
      unit: `/ ${total || "—"}`,
      icon: "PlayCircle",
      hint: "Districts with an APR record this year",
      progress: pct(summary.districtsStarted, total),
    },
    {
      title: "Regions started",
      value: String(summary.regionsStarted),
      unit: "regions",
      icon: "MapPinned",
      hint: "Regions with at least one started district",
    },
    {
      title: "Submitted to RCC",
      value: String(summary.districtsSubmittedRcc),
      unit: "districts",
      icon: "Send",
      hint: "Pending or later in the workflow",
      progress: pct(summary.districtsSubmittedRcc, total),
    },
    {
      title: "Regions with NDPC",
      value: String(summary.regionsSubmittedNdpc),
      unit: "regions",
      icon: "Landmark",
      hint: "Regions with Completed / Closed districts",
    },
    {
      title: "Closed reports",
      value: String(summary.closedReports),
      unit: "districts",
      icon: "Archive",
      hint: "NDPC-closed district APRs",
      progress: pct(summary.closedReports, total),
    },
  ];

  const closedShare = pct(summary.closedReports, total);

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <ExportToolbar />
      <main className="main-content">
        {/* Hero */}
        <section className="relative mb-6 overflow-hidden rounded-2xl border border-[#16325a]/15 bg-[#16325a] text-white shadow-[0_16px_40px_rgba(22,50,90,0.18)] md:mb-8">
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(ellipse at 12% 20%, rgba(13,148,136,0.35), transparent 45%), radial-gradient(ellipse at 88% 10%, rgba(217,119,6,0.22), transparent 40%)",
            }}
          />
          <div className="relative flex flex-col gap-5 p-5 md:flex-row md:items-end md:justify-between md:p-7">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/85">
                <Icon name="BarChart3" size={14} color="#fff" />
                National APR overview
              </div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl lg:text-4xl">
                APR Dashboard
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">
                Track district Progress Report submission from drafting through RCC review to NDPC
                close-out for the selected year.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {WORKFLOW_STEPS.map((step, index) => (
                  <div
                    key={step.key}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs text-white/90"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-[10px] font-bold">
                      {index + 1}
                    </span>
                    <span className="font-semibold">{step.label}</span>
                    <span className="hidden text-white/60 sm:inline">· {step.hint}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full shrink-0 rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm md:w-60">
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70">
                Year under review
              </label>
              <select
                className="w-full rounded-xl border border-white/20 bg-white px-3 py-2.5 text-sm font-semibold text-[#16325a] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300/50"
                value={reportingYear}
                onChange={(e) => setReportingYear(e.target.value)}
              >
                {APR_YEAR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-3 text-xs text-white/65">
                {total ? `${total} districts in national scope` : "Loading district coverage…"}
              </p>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <Icon name="AlertCircle" size={18} color="#be123c" />
            <span>{error}</span>
          </div>
        )}

        {loading && districtRows.length > 0 && (
          <div className="mb-6 rounded-2xl border border-[#16325a]/12 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(22,50,90,0.05)] md:px-5">
            <div className="mb-2 flex items-center justify-between gap-3 text-sm">
              <span className="inline-flex items-center gap-2 text-slate-600">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#16325a] border-t-transparent" />
                Loading district APR statuses for {reportingYear}…
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-[#16325a]">
                {fetchProgress.percent}%
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full transition-[width] duration-300 ease-out"
                style={{
                  width: `${fetchProgress.percent}%`,
                  background: `linear-gradient(90deg, ${DPAT_BRAND}, ${DPAT_CHART.teal})`,
                }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {fetchProgress.loaded} of {fetchProgress.total || districtRows.length} districts
            </p>
          </div>
        )}

        {!treeReady && (
          <p className="mb-6 text-sm text-slate-500">Loading organisation tree…</p>
        )}

        {treeReady && districtRows.length === 0 && (
          <p className="mb-6 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            No districts found in the location tree — KPIs need national district coverage in
            LocationContext.
          </p>
        )}

        {statusReady && (
          <div className="mb-6 flex flex-wrap gap-2">
            {statusChips.map((chip) => {
              const active = statusFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() =>
                    setStatusFilter((prev) => (prev === chip.key ? "all" : chip.key))
                  }
                  className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold transition-all ${
                    active
                      ? "border-transparent text-white shadow-md"
                      : "border-slate-200 bg-white text-slate-700 hover:border-[#16325a]/25 hover:bg-[#16325a]/5"
                  }`}
                  style={active ? { backgroundColor: chip.color, borderColor: chip.color } : undefined}
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: active ? "#fff" : chip.color }}
                  />
                  {chip.label}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {chip.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {loading && districtRows.length > 0
            ? Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-36 animate-pulse rounded-2xl border border-slate-200 bg-white"
                />
              ))
            : headlineMetrics.map((m, i) => <MetricCard key={i} styleIndex={i} {...m} />)}
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="h-full overflow-hidden rounded-2xl border border-[#16325a]/12 bg-white p-4 shadow-[0_8px_24px_rgba(22,50,90,0.05)] md:p-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#16325a]/70">
                    Workflow snapshot
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-[#16325a] md:text-xl">
                    Report performance by stage
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    District counts for {reportingYear} across the APR workflow.
                  </p>
                </div>
                {Number.isFinite(closedShare) ? (
                  <div className="rounded-xl border border-teal-100 bg-teal-50/80 px-3 py-2 text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-700">
                      Closed share
                    </p>
                    <p className="text-lg font-bold tabular-nums text-teal-800">{closedShare}%</p>
                  </div>
                ) : null}
              </div>

              <div className="h-72 w-full md:h-80">
                {loading && districtRows.length > 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    Loading chart…
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={summary.trendBars}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                      <XAxis
                        dataKey="stage"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        interval={0}
                        angle={-12}
                        height={56}
                        textAnchor="end"
                        axisLine={{ stroke: "rgba(22,50,90,0.15)" }}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(22,50,90,0.04)" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Districts" radius={[6, 6, 0, 0]} maxBarSize={56}>
                        {(summary.trendBars || []).map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={STAGE_BAR_COLORS[index % STAGE_BAR_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <KPIPanel
              title="Report KPIs"
              subtitle="Rates vs all districts in scope for the selected year"
              kpis={summary.reportKpis}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="overflow-hidden rounded-2xl border border-[#16325a]/12 bg-white shadow-[0_8px_24px_rgba(22,50,90,0.05)]">
            <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50/80 to-white px-4 py-4 md:px-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-[#16325a]">Districts done (Closed)</h3>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {summary.districtsDoneList.length} district(s) — NDPC closed
                  </p>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10">
                  <Icon name="CheckCircle2" size={18} color={DPAT_CHART.emerald} />
                </div>
              </div>
            </div>
            <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
              {!statusReady ? (
                <li className="px-4 py-8 text-sm text-slate-500">
                  {loading ? "Loading…" : "Load data by selecting a year with districts in scope."}
                </li>
              ) : summary.districtsDoneList.length === 0 ? (
                <li className="px-4 py-8 text-center text-sm text-slate-500">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                    <Icon name="Inbox" size={18} color="#64748b" />
                  </div>
                  No closed districts yet.
                </li>
              ) : (
                summary.districtsDoneList.map((d) => (
                  <li
                    key={d.districtId}
                    className="flex flex-col gap-0.5 px-4 py-3 text-sm transition-colors hover:bg-emerald-50/40 md:flex-row md:items-center md:justify-between"
                  >
                    <span className="font-medium text-slate-800">{d.name}</span>
                    <span className="text-xs text-slate-500 md:text-sm">{d.regionName}</span>
                  </li>
                ))
              )}
            </ul>
          </section>

          <section className="overflow-hidden rounded-2xl border border-[#16325a]/12 bg-white shadow-[0_8px_24px_rgba(22,50,90,0.05)]">
            <div className="border-b border-slate-100 bg-gradient-to-r from-teal-50/80 to-white px-4 py-4 md:px-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-[#16325a]">
                    Regions done (all districts Closed)
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Every district under the region has APR status Closed
                  </p>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-500/10">
                  <Icon name="Map" size={18} color={DPAT_CHART.teal} />
                </div>
              </div>
            </div>
            <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
              {!statusReady ? (
                <li className="px-4 py-8 text-sm text-slate-500">
                  {loading ? "Loading…" : "No data."}
                </li>
              ) : summary.regionsDoneList.length === 0 ? (
                <li className="px-4 py-8 text-center text-sm text-slate-500">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                    <Icon name="Inbox" size={18} color="#64748b" />
                  </div>
                  No region fully closed yet.
                </li>
              ) : (
                summary.regionsDoneList.map((name) => (
                  <li
                    key={name}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-800 transition-colors hover:bg-teal-50/40"
                  >
                    <span className="h-2 w-2 rounded-full bg-teal-500" />
                    {name}
                  </li>
                ))
              )}
            </ul>
          </section>
        </div>

        <div className="mt-6">
          <DistrictSubmissionTable
            rows={filteredStatusRows}
            loading={loading && districtRows.length > 0}
            reportingYear={reportingYear}
            regionFilter={regionFilter}
            onRegionFilterChange={setRegionFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            search={search}
            onSearchChange={setSearch}
            regionOptions={regionOptions}
            statusOptions={APR_STATUS_FILTER_OPTIONS}
          />
        </div>
      </main>
    </div>
  );
};

export default AprDashboard;
